import {
  GenerateTtsPreviewDocument,
  type GenerateTtsPreviewQuery,
  type GenerateTtsPreviewQueryVariables,
  TextToSpeechDocument,
  type TextToSpeechMutation,
  type TextToSpeechMutationVariables,
} from "@/generated/graphql";
import { toastAiCredits } from "@/lib/utils/aiCredits";
import { Button, IconButton } from "@ai-teach/ui-web";
import { AZURE_VOICES, DEFAULT_VOICE, OPENAI_VOICES } from "@ai-teach/utils";
import { useLazyQuery, useMutation } from "@apollo/client/react";
import { useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { FiMic, FiX } from "react-icons/fi";

interface TextToSpeechModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentFolderId: string | null;
}

export function TextToSpeechModal({ isOpen, onClose, currentFolderId }: TextToSpeechModalProps) {
  // Input form state
  const [text, setText] = useState("");
  const [filename, setFilename] = useState("");
  const [provider, setProvider] = useState<"openai" | "azure">("azure");
  const [voice, setVoice] = useState(DEFAULT_VOICE);
  const [tags, setTags] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState("");

  // Preview state
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [audioBase64, setAudioBase64] = useState<string | null>(null);
  const [audioDuration, setAudioDuration] = useState<number | null>(null);

  // Preview query
  const [generatePreview, { loading: isGenerating }] = useLazyQuery<
    GenerateTtsPreviewQuery,
    GenerateTtsPreviewQueryVariables
  >(GenerateTtsPreviewDocument, {
    fetchPolicy: "network-only",
  });

  const [saveSpeech, { loading: isSaving }] = useMutation<
    TextToSpeechMutation,
    TextToSpeechMutationVariables
  >(TextToSpeechDocument, {
    refetchQueries: ["GetMediaHouseAudio"],
    onCompleted: (data) => {
      toast.success(`Audio generated successfully: ${data.textToSpeech.filename}`);
      onClose();
    },
    onError: (error) => {
      if (toastAiCredits(error, "ttsSaveToast")) return;
      toast.error(`Failed to generate audio: ${error.message}`);
    },
  });

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => {
        setText("");
        setFilename("");
        setProvider("azure");
        setVoice(DEFAULT_VOICE);
        setTags([]);
        setTagInput("");
        setAudioUrl(null);
        setAudioBlob(null);
        setAudioBase64(null);
        setAudioDuration(null);
      }, 300);
    }
  }, [isOpen]);

  // Clean up blob URL when component unmounts or audio changes
  useEffect(() => {
    return () => {
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [audioUrl]);

  // Update voice when provider changes
  useEffect(() => {
    if (provider === "openai") {
      setVoice("alloy");
    } else {
      setVoice(DEFAULT_VOICE);
    }
  }, [provider]);

  const handleAddTag = () => {
    const trimmed = tagInput.trim();
    if (trimmed && !tags.includes(trimmed)) {
      setTags([...tags, trimmed]);
      setTagInput("");
    }
  };

  const handleRemoveTag = (tag: string) => {
    setTags(tags.filter((t) => t !== tag));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleGeneratePreview = async () => {
    if (!text.trim()) {
      toast.error("Please enter text to convert");
      return;
    }

    try {
      const { data, error } = await generatePreview({
        variables: {
          text,
          voice,
          provider,
        },
      });

      // Rethrown as-is: its `extensions.code` is what identifies a spent AI balance downstream.
      if (error) {
        throw error;
      }

      if (!data?.generateTTSPreview.success) {
        throw new Error(data?.generateTTSPreview.error || "TTS generation failed");
      }

      if (!data.generateTTSPreview.audioBase64) {
        throw new Error("No audio data returned");
      }

      // Convert base64 to blob
      const binaryString = atob(data.generateTTSPreview.audioBase64);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: "audio/mpeg" });
      const url = URL.createObjectURL(blob);

      // Clean up old URL if exists
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }

      setAudioBlob(blob);
      setAudioBase64(data.generateTTSPreview.audioBase64);
      setAudioUrl(url);
      setAudioDuration(data.generateTTSPreview.duration || null);
      toast.success("Audio preview generated!");
    } catch (error) {
      if (toastAiCredits(error, "ttsPreviewToast")) return;
      toast.error(error instanceof Error ? error.message : "Failed to generate audio");
    }
  };

  const handleSave = async () => {
    if (!audioBlob) {
      toast.error("Please generate audio preview first");
      return;
    }

    if (!filename.trim()) {
      toast.error("Please enter a filename");
      return;
    }

    if (tags.length === 0) {
      toast.error("Please add at least one tag");
      return;
    }

    await saveSpeech({
      variables: {
        input: {
          text,
          filename,
          voice,
          provider,
          tags,
          folderId: currentFolderId || undefined,
          audioData: audioBase64 || undefined,
        },
      },
    });
  };

  const voices = provider === "openai" ? OPENAI_VOICES : AZURE_VOICES;

  if (!isOpen) return null;

  return (
    <div
      className="!mt-0 fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={isGenerating || isSaving ? undefined : onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="text-to-speech-title"
    >
      <div
        className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-lg bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 z-10 flex items-center justify-between border-b bg-white px-6 py-4">
          <div className="flex items-center gap-2">
            <FiMic className="h-5 w-5 text-accent-600" />
            <h2 id="text-to-speech-title" className="h6">
              Generate Audio from Text
            </h2>
          </div>
          <IconButton
            onClick={onClose}
            variant="ghost"
            className="rounded-full p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            aria-label="Close"
            disabled={isGenerating || isSaving}
          >
            <FiX className="h-5 w-5" />
          </IconButton>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Audio Preview (shows when generated) */}
          {audioUrl && (
            <section>
              <label className="mb-2 block label">Audio preview</label>
              <div className="rounded-md border border-gray-200 p-3 bg-gray-50">
                <audio controls className="w-full" src={audioUrl}>
                  <track kind="captions" />
                </audio>
                {audioDuration && (
                  <p className="mt-2 text-xs text-gray-500">
                    Duration: {Math.round(audioDuration)} seconds
                  </p>
                )}
              </div>
            </section>
          )}

          {/* Filename Input (shows after preview) */}
          {audioUrl && (
            <section>
              <label htmlFor="filename" className="mb-2 block label">
                Filename <span className="text-red-500">*</span>
              </label>
              <input
                id="filename"
                type="text"
                value={filename}
                onChange={(e) => setFilename(e.target.value)}
                placeholder="my-audio-file"
                required
                disabled={isGenerating || isSaving}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:bg-gray-100"
              />
              <p className="mt-1 text-xs text-gray-500">
                The file will be saved as {filename || "filename"}.mp3
              </p>
            </section>
          )}

          {/* Text Input */}
          <section>
            <label htmlFor="text" className="mb-2 block label">
              Audio Text <span className="text-red-500">*</span>
            </label>
            <textarea
              id="text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Enter text to convert to speech..."
              rows={6}
              maxLength={5000}
              required
              disabled={isGenerating || isSaving}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:bg-gray-100"
            />
            <p className="mt-1 text-xs text-gray-500">{text.length}/5000 characters</p>
          </section>

          {/* Provider Selector */}
          <section>
            <label className="mb-2 block label">
              TTS Provider <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="provider"
                  value="azure"
                  checked={provider === "azure"}
                  onChange={() => setProvider("azure")}
                  disabled={isGenerating || isSaving}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm">Azure (Multilingual HD, SSML)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="provider"
                  value="openai"
                  checked={provider === "openai"}
                  onChange={() => setProvider("openai")}
                  disabled={isGenerating || isSaving}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm">OpenAI (Better Context)</span>
              </label>
            </div>
            <p className="mt-1 text-xs text-gray-500">
              {provider === "azure"
                ? "Azure: Hundreds of voices with SSML control"
                : "OpenAI: Better at homographs and contextual pronunciation"}
            </p>
          </section>

          {/* Voice Selector */}
          <section>
            <label htmlFor="voice" className="mb-2 block label">
              Voice <span className="text-red-500">*</span>
            </label>
            <select
              id="voice"
              value={voice}
              onChange={(e) => setVoice(e.target.value)}
              disabled={isGenerating || isSaving}
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:bg-gray-100"
            >
              {voices.map((v) => (
                <option key={v.value} value={v.value}>
                  {v.label} · {v.detail}
                </option>
              ))}
            </select>
          </section>

          {/* Generate Preview Button */}
          <Button
            onClick={handleGeneratePreview}
            disabled={isGenerating || isSaving || !text.trim()}
            variant="primary"
            className="w-full bg-accent-600 hover:bg-accent-700"
          >
            {isGenerating && (
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                <title>Generating</title>
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                  fill="none"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            )}
            {isGenerating ? "Generating..." : "Generate Audio Preview"}
          </Button>

          {/* Tags Section (shows after preview) */}
          {audioUrl && (
            <section>
              <label htmlFor="tag-input" className="mb-2 block label">
                Tags <span className="text-red-500">*</span>
              </label>
              <div className="flex gap-2">
                <input
                  id="tag-input"
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Add a tag and press Enter"
                  disabled={isGenerating || isSaving}
                  className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 disabled:bg-gray-100"
                />
                <Button
                  onClick={handleAddTag}
                  disabled={isGenerating || isSaving || !tagInput.trim()}
                  variant="secondary"
                  size="md"
                >
                  Add
                </Button>
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                {tags.map((tag) => (
                  <span
                    key={tag}
                    className="inline-flex items-center gap-1 rounded-full bg-primary-100 px-3 py-1 text-sm text-primary-700"
                  >
                    {tag}
                    <IconButton
                      onClick={() => handleRemoveTag(tag)}
                      disabled={isGenerating || isSaving}
                      variant="ghost"
                      size="sm"
                      aria-label={`Remove tag ${tag}`}
                      className="h-4 w-4 p-0 hover:text-primary-900"
                    >
                      ×
                    </IconButton>
                  </span>
                ))}
              </div>
              <p className="mt-1 text-xs text-gray-500">
                {tags.length === 0
                  ? "Add at least one tag"
                  : `${tags.length} tag${tags.length > 1 ? "s" : ""} added`}
              </p>
            </section>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 border-t bg-white px-6 py-4 flex justify-end gap-3">
          <Button onClick={onClose} variant="secondary" disabled={isGenerating || isSaving}>
            Cancel
          </Button>
          {audioUrl && (
            <Button
              onClick={handleSave}
              disabled={isGenerating || isSaving || !filename.trim() || tags.length === 0}
              variant="primary"
            >
              {isSaving && (
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <title>Saving</title>
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                    fill="none"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              )}
              {isSaving ? "Saving..." : "Save to Library"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
